import React from 'react';
import { motion } from 'framer-motion';
import { Box, Container, Typography, Grid, Paper, Chip, Avatar } from '@mui/material';
import { Work, CalendarToday, LocationOn, Business } from '@mui/icons-material';

const Experience = () => {
  const experiences = [
    {
      id: 1,
      company: "Barclays",
      position: "Assistant Vice President",
      duration: "Dec 2023 - Present",
      location: "NJ, USA · Hybrid",
      description: "Currently leading backend and system integrations in a hybrid enterprise environment.",
      technologies: ["Kafka", "Spring Boot", "Microservices", "ReactJS", "NodeJS", "Angular","Cloud Services"],
      logo: require("../Assests/BARCLAYS.png"),
      featured: true
    },
    {
      id: 2,
      company: "A-G Associates",
      position: "Software Engineer",
      duration: "May - Dec 2023",
      location: "MD, USA · On-site",
      description:
        "Utilized Entity Framework to build and manage the data access layer, ensuring efficient data retrieval and manipulation. Developed and maintained robust web applications using .NET Core, Angular and SQL Server technologies. Implemented user authentication and authorizations mechanisms using ASP.NET Identity, ensuring secure access to sensitive data features. Created and managed interactive reports and dashboards using SQL Server Reporting Services (SSRS). Successfully integrated third-party API’s and services to extend application functionality and improve user experience. Actively participated in code reviews, offering constructive feedback, and ensuring adherence to coding standards and best practices.Collaborated with Quality Assurance team to identify and resolve software defects, enhancing the overall software quality.",
      technologies: [
        ".NET Core",
        "Angular",
        "SQL Server",
        "SSRS",
        "ASP.NET Identity",
        "GitHub"
      ],
      logo: require("../Assests/ag.jpeg"),
      featured: false
    },
    // {
    //   id: 3,
    //   company: "UMBC",
    //   position: "Graduate Assistant",
    //   duration: "2022 - 2023",
    //   location: "MD USA · On-site",
    //   description:
    //     "Supported teaching and research in CS department. Gained experience in GCP, AI/ML, data structures, and backend systems.",
    //   technologies: [
    //     "GCP",
    //     "Machine Learning",
    //     "Artificial Intelligence",
    //     "Data Science",
    //     "Data Structures"
    //   ],
    //   logo: require("../Assests/umbc.png"),
    //   featured: false
    // },
    {
      id: 5,
      company: "Temenos/Kony Inc.",
      position: "Software Development Engineer",
      duration: "2020 - 2021",
      location: "Hyd, India · On-site",
      description:
        "Developed and maintained AppFactory platform, integrated Salesforce services, improved licensing, caching, testing, and made PCI-compliant enhancements across the product line.",
      technologies: [
        "Java",
        "Spring Boot",
        "PHP",
        "Kony Visualizer",
        "AWS",
        "Appium",
        "Imagemagick"
      ],
      logo: require("../Assests/temenos.jpg"),
      featured: false
    },
    {
      id: 6,
      company: "Abhibus",
      position: "Software Engineer",
      duration: "2019 - 2020",
      location: "Hyd, India · On-site",
      description:
        "Developed and maintained e-commerce platform features. Implemented responsive design and optimized application performance.",
      technologies: ["Angular", "Java", "Spring Boot", "MySQL", "Redis"],
      logo: require("../Assests/abhibus.png"),
      featured: false
    },
    {
      id: 4,
      company: "MAQ Software",
      position: "Software Development Engineer",
      duration: "2018 - 2019",
      location: "Hyd, India · On-site",
      description:
        "Led development of enterprise web applications using React.js and Node.js. Collaborated with cross-functional teams to deliver high-quality software solutions.",
      technologies: ["React.js", "Node.js", "MongoDB", "AWS", "Docker"],
      logo: require("../Assests/maq.jpeg"),
      featured: false
    },
  ];

  return (
    <Box
      component="section"
      id="experience"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'var(--bg-secondary)',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'var(--font-display)',
                fontSize: { xs: '2rem', md: '3rem' },
                fontWeight: 700,
                mb: 2,
                color: 'var(--text-primary)',
              }}
            >
              Work Experience
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.125rem',
                color: 'var(--text-secondary)',
                maxWidth: '600px',
                mx: 'auto',
                lineHeight: 1.7,
              }}
            >
              My professional journey in software development, 
              where I've had the opportunity to work on exciting projects and grow my skills.
            </Typography>
          </Box>
        </motion.div>

        {/* Experience Cards */}
        <Grid container spacing={4} alignItems="stretch">
          {experiences.map((experience, index) => (
            <Grid item xs={12} md={6} lg={4} key={experience.id} style={{ display: 'flex' }}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                style={{ flex: 1, display: 'flex' }}
              >
                <Paper
                  elevation={experience.featured ? 8 : 2}
                  sx={{
                    p: 4,
                    height: 180,
                    minHeight: 180,
                    maxHeight: 180,
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    borderRadius: 'var(--border-radius)',
                    border: '1px solid var(--border)',
                    background: experience.featured 
                      ? 'linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%)'
                      : 'var(--bg-primary)',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'var(--transition)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 'var(--shadow-xl)',
                    },
                    ...(experience.featured && {
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: 'linear-gradient(90deg, var(--primary) 0%, var(--secondary) 100%)',
                      },
                    }),
                  }}
                >
                  {/* Company Logo, Title, etc. */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar
                      src={experience.logo}
                      alt={experience.company}
                      sx={{
                        width: 60,
                        height: 60,
                        mr: 2,
                        border: '2px solid var(--border)',
                      }}
                    >
                      <Business />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: 'var(--text-primary)',
                          mb: 0.5,
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          maxWidth: 200,
                          display: 'block',
                        }}
                      >
                        {experience.company}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'var(--primary)',
                          fontWeight: 600,
                        }}
                      >
                        {experience.position}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Duration and Location */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarToday sx={{ fontSize: 16, color: 'var(--text-muted)' }} />
                      <Typography
                        variant="body2"
                        sx={{ color: 'var(--text-muted)', fontWeight: 500 }}
                      >
                        {experience.duration}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn sx={{ fontSize: 16, color: 'var(--text-muted)' }} />
                      <Typography
                        variant="body2"
                        sx={{ color: 'var(--text-muted)', fontWeight: 500 }}
                      >
                        {experience.location}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Description */}
                  {/* <Typography
                    variant="body1"
                    sx={{
                      color: 'var(--text-secondary)',
                      mb: 3,
                      lineHeight: 1.6,
                    }}
                  >
                    {experience.description}
                  </Typography> */}

                  {/* Technologies */}
                  {/* <Box sx={{ mt: 'auto' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        mb: 2,
                      }}
                    >
                      Technologies used:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {experience.technologies.map((tech) => (
                        <Chip
                          key={tech}
                          label={tech}
                          size="small"
                          sx={{
                            backgroundColor: 'var(--bg-secondary)',
                            color: 'var(--text-primary)',
                            border: '1px solid var(--border)',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                          }}
                        />
                      ))}
                    </Box>
                  </Box> */}

                  {/* Featured Badge */}
                  {experience.featured && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        backgroundColor: 'var(--primary)',
                        color: 'white',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 'var(--border-radius-sm)',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Current
                    </Box>
                  )}
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Experience; 